/**
 * 认证相关API接口
 */
import axios from "axios";
import { API_BASE_URL } from "./config";
import { getBrowserFingerprint, getDeviceId } from "../utils/deviceFingerprint";
import { clearStoredActiveOrganizationId } from "../utils/organizationScope";
import { redirectToPortalLogin } from "../utils/authRedirect";
import {
    clearAuthState,
    getAccessToken,
    getAuthHeader as getStoredAuthHeader,
    getRefreshToken,
    hasFreshAccessToken,
    readAuthSnapshot,
    writeLoginAuthState,
} from "./authState";
import { refreshAuthSession } from "./authRefreshOrchestrator";

// 创建axios实例
const authApi = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

const logAuthError = (label, error) => {
    console.error(label, {
        status: error?.response?.status,
        code: error?.response?.data?.detail?.code || error?.code,
    });
};

const getSafeRefreshDiagnostics = () => {
    const snapshot = readAuthSnapshot();
    return {
        hasRefreshToken: Boolean(snapshot.refreshToken),
        selectedStorage: snapshot.storageName || "none",
        sessionEpoch: snapshot.meta?.sessionEpoch || null,
        revision: snapshot.meta?.revision || null,
    };
};

const shouldLogAuthDebug = () => {
    try {
        return localStorage.getItem("flymeta.auth_debug") === "1";
    } catch {
        return false;
    }
};

// 请求拦截器 - 添加token到请求头
authApi.interceptors.request.use(
    (config) => {
        // 先从localStorage检查，再从sessionStorage检查
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error),
);

// 响应拦截器
authApi.interceptors.response.use(
    (response) => {
        // 直接返回响应数据
        return response;
    },
    (error) => {
        logAuthError("API请求错误:", error);
        return Promise.reject(error);
    },
);

/**
 * 获取验证码
 * @returns {Promise} 包含验证码ID和图片base64编码的Promise
 */
export const getCaptcha = async () => {
    try {
        const response = await authApi.get("/auth/captcha", {
            timeout: 60000, // 10秒超时
        });
        return response.data;
    } catch (error) {
        logAuthError("获取验证码失败:", error);
        throw error;
    }
};

/**
 * 用户登录
 * @param {Object} credentials - 登录凭证
 * @param {string} credentials.username - 用户名
 * @param {string} credentials.password - 密码
 * @param {string} [credentials.captchaId] - 验证码ID，仅风险态需要
 * @param {string} [credentials.captchaCode] - 验证码，仅风险态需要
 * @param {boolean} credentials.remember - 是否记住登录状态
 * @returns {Promise} 登录结果
 */
export const login = async (credentials) => {
    try {
        // 构建表单数据
        const formData = new URLSearchParams();
        formData.append("username", credentials.username);
        formData.append("password", credentials.password);
        if (credentials.captchaId || credentials.captchaCode) {
            formData.append("captcha_id", credentials.captchaId || "");
            formData.append("captcha_code", credentials.captchaCode || "");
        }
        try {
            const deviceId = getDeviceId();
            if (deviceId) {
                formData.append("device_id", deviceId);
            }
        } catch {
            // Device signals are optional in shadow mode, so login must continue.
        }
        try {
            const browserFingerprint = await getBrowserFingerprint();
            if (browserFingerprint?.hash) {
                formData.append("fingerprint", browserFingerprint.hash);
                formData.append("fingerprint_version", String(browserFingerprint.version));
            }
        } catch {
            // Fingerprint collection failure must not affect authentication.
        }

        const response = await axios.post(`${API_BASE_URL}/auth/login`, formData, {
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            // 增加超时时间
            timeout: 15000,
        });

        writeLoginAuthState(response.data, { remember: credentials.remember, entrypoint: "login" });

        return response.data;
    } catch (error) {
        // 增强错误日志
        logAuthError("登录失败:", error);

        // 提供更详细的网络错误信息
        if (error.code === "ERR_BAD_RESPONSE" && error.response?.status === 502) {
            throw new Error("服务器网关错误(502)：后端服务可能暂时不可用，请稍后再试");
        } else if (error.code === "ECONNABORTED") {
            throw new Error("请求超时：服务器响应时间过长，请稍后再试");
        } else if (!error.response) {
            throw new Error("网络错误：无法连接到服务器，请检查您的网络连接");
        }

        throw error;
    }
};

/**
 * 刷新访问令牌
 * @returns {Promise} 刷新结果
 */
export const refreshToken = async (options = {}) => {
    return refreshAuthSession({
        entrypoint: options.entrypoint || "direct",
        force: options.force !== false,
    }).catch((error) => {
        if (shouldLogAuthDebug()) {
            console.info("[auth-debug] refresh token failure", {
                status: error?.response?.status,
                responseDetail: error?.response?.data?.detail || null,
                errorCode: error?.code,
                errorMessage: error?.message,
                ...getSafeRefreshDiagnostics(),
            });
        }
        throw error;
    });
};

/**
 * 用户登出
 * 通知后端撤销当前 refresh session，无论后端调用成功与否都清空本地凭证并跳转登录页。
 */
export const logout = async () => {
    // 先调用后端 logout 端点撤销当前 refresh session，使旧 token 立即失效。
    // 后端调用失败不阻塞本地清理（网络异常等情况仍保证用户能登出）。
    try {
        const refreshTokenValue = getRefreshToken();
        if (refreshTokenValue) {
            await axios.post(`${API_BASE_URL}/auth/logout`, {
                refresh_token: refreshTokenValue,
            });
        }
    } catch (error) {
        logAuthError("登出请求失败:", error);
    }

    clearAuthState({ entrypoint: "logout" });
    clearStoredActiveOrganizationId();

    // 重定向到 Portal 登录页面
    redirectToPortalLogin();
};

/**
 * 获取当前用户信息
 * @returns {Promise} 用户信息
 */
export const getCurrentUser = async () => {
    try {
        const response = await authApi.get("/auth/me");
        return response.data;
    } catch (error) {
        logAuthError("获取用户信息失败:", error);
        throw error;
    }
};

/**
 * 检查用户是否已登录
 * @returns {boolean} 是否已登录
 */
export const isAuthenticated = () => {
    return hasFreshAccessToken();
};

/**
 * 注册新用户
 * @param {Object} userData - 用户数据
 * @param {string} userData.username - 用户名
 * @param {string} userData.email - 邮箱
 * @param {string} userData.password - 密码
 * @param {string} userData.fullName - 全名(可选)
 * @param {string} userData.captchaId - 验证码ID
 * @param {string} userData.captchaCode - 验证码
 * @returns {Promise} 注册结果
 */
export const register = async (userData) => {
    try {
        const response = await authApi.post("/auth/register", {
            username: userData.username,
            email: userData.email,
            password: userData.password,
            full_name: userData.fullName,
            captcha_id: userData.captchaId,
            captcha_code: userData.captchaCode,
        });
        return response.data;
    } catch (error) {
        logAuthError("注册失败:", error);
        throw error;
    }
};

/**
 * 修改密码
 * @param {string} oldPassword - 旧密码
 * @param {string} newPassword - 新密码
 * @returns {Promise} 修改结果
 */
export const changePassword = async (oldPassword, newPassword) => {
    try {
        const response = await authApi.post("/auth/change-password", {
            old_password: oldPassword,
            new_password: newPassword,
        });
        return response.data;
    } catch (error) {
        logAuthError("修改密码失败:", error);
        throw error;
    }
};

/**
 * 获取认证头信息
 * @returns {Object} 认证头信息
 */
export const getAuthHeader = () => {
    return getStoredAuthHeader();
};
